import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Field, Label, Select } from '@shared/ui/Input';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { MultiFileList } from '@shared/ui/MultiFileList';
import { ACCEPT_CONTRACT_SET } from '@shared/lib/fileAccept';
import { IconTrash } from '@shared/ui/icons';
import {
  useUploadContractVersionMutation,
  useDeleteContractVersionMutation,
  useAnalyzeContractMutation,
  useCancelContractAnalysisMutation,
} from '@shared/api';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { analysisStarted } from '@features/analysisProgress/model/slice';
import { AnalysisProgressBar } from '../AnalysisProgressBar';
import { VersionDiff } from '../VersionDiff';
import {
  Empty,
  IconDangerButton,
  Row,
  Section,
  VersionControls,
} from './styles';

const ProgressWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const UploadForm = styled.form`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const UploadField = styled(Field)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

import type { ContractVersion } from '@entities/legal/types';

interface ContractVersionsSectionProps {
  contractId: string;
  contractStatus: string;
  versions: ContractVersion[];
  previousRisksCount: number;
  can_write: boolean;
}

export function ContractVersionsSection({
  contractId,
  contractStatus,
  versions,
  previousRisksCount,
  can_write,
}: ContractVersionsSectionProps) {
  const dispatch = useAppDispatch();
  const [uploadVersion, { isLoading: isUploading }] = useUploadContractVersionMutation();
  const [deleteVersion] = useDeleteContractVersionMutation();
  const [analyze, { isLoading: isAnalyzing }] = useAnalyzeContractMutation();
  const [cancelAnalysis, { isLoading: isCancelling }] = useCancelContractAnalysisMutation();

  const [showUpload, setShowUpload] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [source, setSource] = useState<'counterparty' | 'company'>('company');
  const [deletingVersionId, setDeletingVersionId] = useState<string | null>(null);
  const [versionDeleteError, setVersionDeleteError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const hasLocalProgress = useAppSelector((s) => !!s.analysisProgress.byContract[contractId]);

  async function onUploadVersion(e: FormEvent) {
    e.preventDefault();
    if (files.length === 0) return;
    const form = new FormData();
    for (const f of files) {
      form.append('file', f);
    }
    form.append('source', source);
    await uploadVersion({ contractId, form }).unwrap();
    dispatch(analysisStarted({ contract_id: contractId }));
    setFiles([]);
    setShowUpload(false);
  }

  async function onDeleteVersion(versionId: string, versionNo: number) {
    if (
      !window.confirm(
        `Удалить версию v${versionNo}? Вместе с ней удалятся её файлы и риски этой редакции. Действие необратимо.`,
      )
    ) {
      return;
    }
    setVersionDeleteError(null);
    setDeletingVersionId(versionId);
    try {
      await deleteVersion({ contractId, versionId }).unwrap();
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось удалить версию. Попробуйте ещё раз.';
      setVersionDeleteError(msg);
    } finally {
      setDeletingVersionId(null);
    }
  }

  async function onRerun() {
    dispatch(analysisStarted({ contract_id: contractId }));
    await analyze(contractId).unwrap();
  }

  async function onCancel() {
    if (!window.confirm('Остановить текущий анализ?')) return;
    setCancelError(null);
    try {
      await cancelAnalysis(contractId).unwrap();
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось остановить анализ. Попробуйте ещё раз.';
      setCancelError(msg);
    }
  }

  return (
    <>
      {(contractStatus === 'under_review' || hasLocalProgress) && (
        <ProgressWrapper>
          <AnalysisProgressBar contractId={contractId} previousRisks={previousRisksCount} />
        </ProgressWrapper>
      )}

      <Section>
        <h4>Версии ({versions.length})</h4>
        <VersionControls>
          <Button
            type="button"
            onClick={() => setShowUpload((v) => !v)}
            disabled={!can_write && !showUpload}
            title={!can_write ? 'Только чтение' : undefined}
          >
            {showUpload ? 'Отменить' : 'Загрузить новую версию'}
          </Button>
          {versions.length > 0 && (
            <Button
              type="button"
              onClick={onRerun}
              disabled={
                isAnalyzing ||
                contractStatus === 'under_review' ||
                contractStatus === 'parsing' ||
                hasLocalProgress ||
                !can_write
              }
              title={
                !can_write
                  ? 'Только чтение'
                  : 'Неразобранные файлы комплекта сначала будут разобраны, затем — свежий анализ'
              }
            >
              {isAnalyzing
                ? 'Запуск…'
                : contractStatus === 'parsing'
                  ? 'Идёт разбор файлов…'
                  : contractStatus === 'under_review' || hasLocalProgress
                    ? 'Идёт перепроверка…'
                    : contractStatus === 'parse_failed'
                      ? 'Повторить разбор'
                      : 'Перезапустить анализ'}
            </Button>
          )}
          {(contractStatus === 'under_review' || contractStatus === 'parsing') && (
            <Button
              type="button"
              $variant="danger"
              onClick={onCancel}
              disabled={isCancelling || !can_write}
              title={!can_write ? 'Только чтение' : 'Остановить текущий анализ договора'}
            >
              {isCancelling ? 'Останавливаем…' : 'Остановить анализ'}
            </Button>
          )}
        </VersionControls>

        {cancelError && (
          <Empty role="alert" $danger>
            {cancelError}
          </Empty>
        )}

        {showUpload && (
          <UploadForm onSubmit={onUploadVersion}>
            <FileDropZone
              multiple
              accept={ACCEPT_CONTRACT_SET}
              hint="Новая редакция (контрагент или мы) — можно сразу весь комплект: договор, приложения, спецификации"
              onFiles={(picked) =>
                setFiles((prev) => {
                  const seen = new Set(prev.map((f) => `${f.name}::${f.size}`));
                  const next = [...prev];
                  for (const f of picked) {
                    const key = `${f.name}::${f.size}`;
                    if (!seen.has(key)) {
                      next.push(f);
                      seen.add(key);
                    }
                  }
                  return next;
                })
              }
            />
            <MultiFileList files={files} onRemove={(idx) => setFiles((prev) => prev.filter((_, i) => i !== idx))} />
            <UploadField>
              <Label htmlFor="vsrc">Источник версии</Label>
              <Select id="vsrc" value={source} onChange={(e) => setSource(e.target.value as 'counterparty' | 'company')}>
                <option value="company">Наша редакция</option>
                <option value="counterparty">От контрагента</option>
              </Select>
            </UploadField>
            <Button
              type="submit"
              disabled={files.length === 0 || isUploading || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isUploading ? 'Загрузка и анализ…' : `Загрузить${files.length > 0 ? ` (${files.length})` : ''}`}
            </Button>
          </UploadForm>
        )}

        {versionDeleteError && (
          <Empty role="alert" $danger>
            {versionDeleteError}
          </Empty>
        )}

        {versions.length === 0 ? (
          <Empty>Версий пока нет.</Empty>
        ) : (
          versions.map((v) => (
            <Row key={v.id}>
              <span>
                v{v.version_no} · {v.source === 'counterparty' ? 'от контрагента' : 'наша'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {new Date(v.uploaded_at).toLocaleString('ru-RU')}
                {can_write && (
                  <IconDangerButton
                    type="button"
                    aria-label={`Удалить версию v${v.version_no}`}
                    title={`Удалить версию v${v.version_no}`}
                    disabled={deletingVersionId === v.id}
                    onClick={() => onDeleteVersion(v.id, v.version_no)}
                  >
                    <IconTrash size={14} />
                  </IconDangerButton>
                )}
              </span>
            </Row>
          ))
        )}
      </Section>

      {versions.length >= 2 && (
        <Section>
          <h4>Сравнение редакций</h4>
          <VersionDiff contractId={contractId} versions={versions} />
        </Section>
      )}
    </>
  );
}
